import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * App-wide ErrorBoundary.
 * Prevents a single render error from white-screening the whole app
 * (critical for the iOS/Android Capacitor build — without this the user
 * has no way to recover except force-closing the app).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log so we can read it from console / Xcode device logs
    console.error("🛑 ErrorBoundary caught:", error, info.componentStack);
  }

  handleReload = () => {
    // Clear potentially-corrupt round state but keep auth & onboarding
    try {
      localStorage.removeItem("settleup_offline_queue");
    } catch {
      /* ignore */
    }
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-background">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-destructive" />
              </div>
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">
                Something went sideways ⛳
              </h1>
              <p className="text-muted-foreground text-sm">
                The app hit an unexpected hiccup. Your saved rounds and stats
                are safe — just reload to get back on the course.
              </p>
            </div>
            {this.state.error?.message && (
              <p className="text-xs text-muted-foreground/70 font-mono bg-muted/50 rounded p-2 break-words">
                {this.state.error.message}
              </p>
            )}
            <Button
              onClick={this.handleReload}
              size="lg"
              className="w-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload App
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
